/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import toNumber from 'lodash/toNumber';
import isArray from 'lodash/isArray';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import type { Dispatch } from 'redux';

import { requestFailure, catchError, status, urlWithParams } from '../utils/utils';
import type { RecaptchaFields } from '../hooks/useRecaptchaBooking';
import {
    GET_RENTAL_EQUIPMENTS,
    GET_RENTAL_EQUIPMENTS_ERROR,
    GET_EQUIPMENT_INFO,
    GET_EQUIPMENT_INFO_ERROR,
    CREATE_RENTALS_RESERVATION,
    CREATE_RENTALS_RESERVATION_ERROR,
    GET_RENTALS_RESERVATION,
    GET_RENTALS_RESERVATION_ERROR,
    UPDATE_RENTALS_RESERVATION,
    UPDATE_RENTALS_RESERVATION_ERROR,
    SAVE_RENTALS_RESERVATIONS,
    RENTAL_BASEPATH,
} from '../constants';

export function fetchRentalEquipments(facilityId: string) {
    return (dispatch: Dispatch) =>
        axios
            .get(
                `${process.env.API}${RENTAL_BASEPATH}/facilities/${facilityId}/equipment`
            )
            .then(status)
            .then((json) => json.data)
            .then((json) => {
                dispatch<any>({ type: GET_RENTAL_EQUIPMENTS, payload: json });
                return Promise.resolve(json);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        requestFailure(
                            GET_RENTAL_EQUIPMENTS_ERROR,
                            'Error fetching campground equipments.'
                        )
                    );
                }
                return Promise.resolve(error);
            });
}

export const fetchRentalEquipmentById = (resourceId: string) => {
    const formattedURL = urlWithParams(
        `${process.env.API}${RENTAL_BASEPATH}/equipment/${resourceId}`
    );
    return (dispatch: Dispatch) =>
        axios
            .get(formattedURL)
            .then(status)
            .then((json) => json.data)
            .then((json) => {
                dispatch({ type: GET_EQUIPMENT_INFO, payload: json });
                return Promise.resolve(json);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        requestFailure(
                            GET_EQUIPMENT_INFO_ERROR,
                            'Error fetching campground equipments.'
                        )
                    );
                }
                return Promise.resolve(error);
            });
};

export function createReservation(
    account: any,
    facilityID: string,
    resourceType: string,
    quantity: string,
    startDate: string,
    endDate: string,
    reservationId: string,
    recaptcha?: RecaptchaFields
) {
    const requestBody: Record<string, any> = {
        account_id: account.account_id,
        facility_id: facilityID,
        resource_id: resourceType,
        quantity: toNumber(quantity),
        date_range: {
            start_date: startDate,
            end_date: endDate,
        },
        reservation_options: {
            required_reservation:
                isEmpty(reservationId) || isNil(reservationId) ? '' : reservationId,
        },
    };

    // The rentals backend reads the reCAPTCHA token from flat request fields.
    if (recaptcha) {
        requestBody.recaptcha_token = recaptcha.recaptcha_token;
        requestBody.recaptcha_version = recaptcha.recaptcha_version;
    }

    return (dispatch: Dispatch) =>
        axios
            .post(`${process.env.API}${RENTAL_BASEPATH}/reservations`, requestBody, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        requestFailure(
                            CREATE_RENTALS_RESERVATION_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                dispatch({
                    type: CREATE_RENTALS_RESERVATION,
                    payload: response.reservation,
                });
                return Promise.resolve(response);
            });
}

export function saveReservations(reservations: any) {
    return (dispatch: Dispatch) => {
        dispatch({ type: SAVE_RENTALS_RESERVATIONS, payload: reservations });
    };
}

export function fetchReservations(reservationIDs: string | Array<string>) {
    let idsStr = '';
    if (isArray(reservationIDs)) {
        forEach(reservationIDs, (id) => {
            idsStr += idsStr === '' ? '?' : '&';
            idsStr += `id=${id}`;
        });
    } else {
        idsStr = `?id=${reservationIDs}`;
    }

    return (dispatch: Dispatch) => {
        return axios
            .get(`${process.env.API}${RENTAL_BASEPATH}/reservations${idsStr}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: GET_RENTALS_RESERVATION_ERROR,
                        payload: error,
                    });
                }
                return Promise.resolve(error);
            })
            .then((response) => {
                dispatch({ type: GET_RENTALS_RESERVATION, payload: response.data });
                return Promise.resolve(response);
            });
    };
}

export const fetchRentalEquipmentAvailability = (
    equipmentId: string,
    start: string,
    end: string
) => {
    return () => {
        // setup the query parameters!
        const dateParams = {
            start_date: start,
            end_date: end,
        };
        // format the URL to be used in 'fetch'
        const formattedURL = urlWithParams(
            `${process.env.API}${RENTAL_BASEPATH}/availability/equipment/${equipmentId}`,
            dateParams
        );
        return axios
            .get(formattedURL)
            .then(status)
            .catch(catchError)
            .then((response) => response.data)
            .then((data) => {
                if (data.err) {
                    return Promise.resolve(data.err);
                }
                return Promise.resolve(data);
            });
    };
};

export const updateReservation = (
    reservationID: string,
    occupantData: any,
    checkin: string,
    checkout: string,
    resourceId: string,
    resourceQuantity: string
) => {
    return (dispatch: Dispatch) =>
        axios
            .put(
                `${process.env.API}${RENTAL_BASEPATH}/reservations/${reservationID}`,
                {
                    check_in: checkin,
                    check_out: checkout,
                    resource_id: resourceId,
                    quantity: resourceQuantity,
                    site_occupant: occupantData,
                },
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then(status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        requestFailure(
                            UPDATE_RENTALS_RESERVATION_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                dispatch({ type: UPDATE_RENTALS_RESERVATION, payload: response });
                return Promise.resolve(response);
            });
};
